<template>
  <component
    :is="baseComponent"
    :to="link.path ? link.path : '/'"
    :class="{ active: isActive }"
    :exact="link.path === '/' || !link.path"
    tag="li"
  >
    <a
      v-if="isMenu"
      class="sidebar-menu-item"
      :aria-expanded="!collapsed"
      data-toggle="collapse"
      @click.prevent="collapseMenu"
    >
      <template v-if="addLink">
        <span class="sidebar-mini-icon">{{ linkPrefix }}</span>
        <span class="sidebar-normal">
          {{ link.name }} <b class="caret"></b>
        </span>
      </template>
      <template v-else>
        <i :class="link.icon"></i>
        <p>{{ link.name }} <b class="caret"></b></p>
      </template>
    </a>

    <collapse-transition>
      <div
        v-if="$slots.default || this.isMenu"
        v-show="!collapsed"
        class="collapse show"
      >
        <ul class="nav">
          <slot></slot>
        </ul>
      </div>
    </collapse-transition>

    <slot
      name="title"
      v-if="children.length === 0 && !$slots.default && link.path"
    >
      <component
        :to="link.path"
        @click.native="linkClick"
        :is="elementType(link, false)"
        :class="{ active: link.active }"
        :target="link.target"
        :href="link.path"
      >
        <template v-if="addLink">
          <span class="sidebar-mini-icon">{{ linkPrefix }}</span>
          <span class="sidebar-normal">{{ link.name }}</span>
        </template>
        <template v-else>
          <div class="emoji-icon-wrapper">
            <span class="svg-icon" v-html="link.icon"></span>
          </div>
          <p class="menu-label">{{ link.name }}</p>
        </template>
      </component>
    </slot>
  </component>
</template>
<script>
import { CollapseTransition } from 'vue2-transitions';

export default {
  name: 'sidebar-item',
  components: {
    CollapseTransition,
  },
  props: {
    menu: {
      type: Boolean,
      default: false,
      description:
        "Whether the item is a menu. Most of the item it's not used and should be used only if you want to override the default behavior.",
    },
    link: {
      type: Object,
      default: () => {
        return {
          name: '',
          path: '',
          children: [],
        };
      },
      description:
        'Sidebar link. Can contain name, path, icon and other attributes. See examples for more info',
    },
  },
  provide() {
    return {
      addLink: this.addChild,
      removeLink: this.removeChild,
    };
  },
  inject: {
    addLink: { default: null },
    removeLink: { default: null },
    autoClose: {
      default: true,
    },
  },
  data() {
    return {
      children: [],
      backgroundColor: 'rgb(61 62 62)',
      collapsed: true,
    };
  },
  computed: {
    baseComponent() {
      return this.isMenu || this.link.isRoute ? 'li' : 'router-link';
    },
    linkPrefix() {
      if (this.link && this.link.name) {
        let words = this.link.name.split(' ');
        return words.map((word) => word.substring(0, 1)).join('');
      }
      return '';
    },
    isMenu() {
      return this.children.length > 0 || this.menu === true;
    },
    isActive() {
      if (this.$route && this.$route.path) {
        let matchingRoute = this.children.find((c) =>
          this.$route.path.startsWith(c.link.path)
        );
        if (matchingRoute !== undefined) {
          return true;
        }
      }
      return false;
    },
  },
  methods: {
    addChild(item) {
      const index = this.$slots.default.indexOf(item.$vnode);
      this.children.splice(index, 0, item);
    },
    removeChild(item) {
      const tabs = this.children;
      const index = tabs.indexOf(item);
      tabs.splice(index, 1);
    },
    elementType(link, isParent = true) {
      if (link.isRoute === false) {
        return isParent ? 'li' : 'a';
      } else {
        return 'router-link';
      }
    },
    linkAbbreviation(name) {
      const matches = name.match(/\b(\w)/g);
      return matches.join('');
    },
    linkClick() {
      if (
        this.autoClose &&
        this.$sidebar &&
        this.$sidebar.showSidebar === true
      ) {
        this.$sidebar.displaySidebar(false);
      }
    },
    collapseMenu() {
      this.collapsed = !this.collapsed;
    },
    collapseSubMenu(link) {
      link.collapsed = !link.collapsed;
    },
  },
  mounted() {
    if (this.addLink) {
      this.addLink(this);
    }
    if (this.link.collapsed !== undefined) {
      this.collapsed = this.link.collapsed;
    }
    if (this.isActive && this.isMenu) {
      this.collapsed = false;
    }
  },
  destroyed() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
    if (this.removeLink) {
      this.removeLink(this);
    }
  },
};
</script>
<style scoped lang="scss">
.sidebar-menu-item {
  cursor: pointer;
}

.emoji-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  float: left;
}

.svg-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e0e0e0;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease;
}

.svg-icon :deep(svg) {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  display: block;
}

.menu-label {
  color: #e0e0e0 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  font-family: 'Poppins', sans-serif !important;
  letter-spacing: 0.3px;
  margin: 0;
  line-height: 38px;
  transition: all 0.3s ease;
}

.sidebar-normal,
.sidebar-menu-item p {
  font-family: 'Poppins', sans-serif !important;
}

// Hover effects
a:hover {
  .emoji-icon-wrapper {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .svg-icon {
    color: #fff;
    transform: scale(1.1);
  }

  .menu-label {
    color: #fff !important;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  }
}

// Active state
.active > a {
  //background: linear-gradient(
  //  90deg,
  //  rgba(255, 141, 114, 0.12) 0%,
  //  rgba(255, 107, 74, 0.05) 100%
  //) !important;
  //box-shadow: inset 3px 0 0 #ff8d72, 0 2px 8px rgba(0, 0, 0, 0.3);
  //border-right: none;
  //position: relative;

  .emoji-icon-wrapper {
    background: rgba(255, 141, 114, 0.15);
    box-shadow: 0 0 20px rgba(255, 141, 114, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 141, 114, 0.4);
    transform: scale(1.05);
  }

  .svg-icon {
    color: #ff8d72;
    transform: scale(1.1);
    filter: drop-shadow(0 0 6px rgba(255, 141, 114, 0.6));
  }

  .menu-label {
    color: #fff !important;
    font-weight: 700 !important;
    text-shadow: 0 0 12px rgba(255, 141, 114, 0.4);
  }

  // Subtle pulse animation for active item
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 60%;
    background: linear-gradient(180deg, transparent, #ff8d72, transparent);
    border-radius: 0 2px 2px 0;
    animation: pulse-glow 2s ease-in-out infinite;
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    opacity: 0.6;
    box-shadow: 0 0 5px rgba(255, 141, 114, 0.3);
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 15px rgba(255, 141, 114, 0.6);
  }
}
</style>
